import { RecommendationItem } from './recommendations';
import { getRecommendationsScope } from './recommendations-scope';

/**
 * Per-rec-type result cache (cross-sell, flavour-me, up-sell, …).
 *
 * Each instance is isolated by `typeKey` + scope, so different rec types
 * cache independently. Multiple `cacheKey` buckets coexist inside one
 * instance — cross-sell/flavour-me use `eventId` as the key; up-sell uses
 * `'<eventId>.<itemId>'` so different items in the same event don't collide.
 *
 * Cross-cutting state — dismissed UUIDs, cart-line attribution — lives in
 * `recommendations-state.ts` instead, so it's shared across every rec type.
 */
const STORAGE_PREFIX = 'fooda_rec_cache';

type StorageData = {
  entries: Record<string, RecommendationItem[]>;
  skipNext: string[];
};

const EMPTY: StorageData = { entries: {}, skipNext: [] };

export type RecommendationsCacheConfig = {
  typeKey: string;
  getScope?: () => string;
};

export type RecommendationsCache = {
  getRecommendations: (cacheKey: string) => RecommendationItem[] | null;
  saveRecommendations: (cacheKey: string, recs: RecommendationItem[]) => void;
  markSkipNext: (cacheKey: string) => void;
  consumeSkipNext: (cacheKey: string) => boolean;
  invalidateKey: (cacheKey: string) => void;
  invalidate: () => void;
  purgeEvent: (eventId: string | number) => void;
};

const keyBelongsToEvent = (cacheKey: string, eventId: string): boolean =>
  cacheKey === eventId || cacheKey.startsWith(`${eventId}.`);

export const createRecommendationsCache = ({
  typeKey,
  getScope = getRecommendationsScope,
}: RecommendationsCacheConfig): RecommendationsCache => {
  const getKey = (): string => `${STORAGE_PREFIX}.${typeKey}.${getScope()}`;

  const read = (): StorageData => {
    try {
      const raw = localStorage.getItem(getKey());
      if (!raw) return EMPTY;
      const parsed = JSON.parse(raw) as Partial<StorageData>;
      return { entries: parsed.entries ?? {}, skipNext: parsed.skipNext ?? [] };
    } catch {
      return EMPTY;
    }
  };

  const write = (data: StorageData): void => {
    try {
      localStorage.setItem(getKey(), JSON.stringify(data));
    } catch {
      // Quota / private mode — losing persistence is acceptable.
    }
  };

  return {
    getRecommendations: (cacheKey) => {
      const entry = read().entries[cacheKey];
      return entry && entry.length ? entry : null;
    },

    saveRecommendations: (cacheKey, recommendations) => {
      const data = read();
      write({ ...data, entries: { ...data.entries, [cacheKey]: recommendations } });
    },

    markSkipNext: (cacheKey) => {
      const data = read();
      if (data.skipNext.includes(cacheKey)) return;
      write({ ...data, skipNext: [...data.skipNext, cacheKey] });
    },

    consumeSkipNext: (cacheKey) => {
      const data = read();
      if (!data.skipNext.includes(cacheKey)) return false;
      write({ ...data, skipNext: data.skipNext.filter((k) => k !== cacheKey) });
      return true;
    },

    invalidateKey: (cacheKey) => {
      const data = read();
      if (!(cacheKey in data.entries) && !data.skipNext.includes(cacheKey)) return;
      const entries = { ...data.entries };
      delete entries[cacheKey];
      write({ entries, skipNext: data.skipNext.filter((k) => k !== cacheKey) });
    },

    invalidate: () => write({ entries: {}, skipNext: [] }),

    purgeEvent: (eventId) => {
      const idStr = String(eventId);
      const data = read();
      const entries: typeof data.entries = {};
      let changed = false;
      for (const [key, value] of Object.entries(data.entries)) {
        if (keyBelongsToEvent(key, idStr)) {
          changed = true;
          continue;
        }
        entries[key] = value;
      }
      const skipNext = data.skipNext.filter((k) => {
        if (keyBelongsToEvent(k, idStr)) {
          changed = true;
          return false;
        }
        return true;
      });
      if (changed) write({ entries, skipNext });
    },
  };
};

export const crossSellCache = createRecommendationsCache({ typeKey: 'cross_sell' });
export const flavourMeCache = createRecommendationsCache({ typeKey: 'flavour_me' });
export const upSellCache = createRecommendationsCache({ typeKey: 'up_sell' });
export const popupCrossSellCache = createRecommendationsCache({ typeKey: 'popup_cross_sell' });
export const popupFlavourMeCache = createRecommendationsCache({ typeKey: 'popup_flavour_me' });
export const popupUpSellCache = createRecommendationsCache({ typeKey: 'popup_up_sell' });
export const homeFlavourMeCache = createRecommendationsCache({ typeKey: 'home_flavour_me' });
export const homePopupFlavourMeCache = createRecommendationsCache({ typeKey: 'home_popup_flavour_me' });

// All product-known caches. Used by the GC pass to iterate every cache when
// purging events past TTL. Add new caches here so they get garbage-collected.
export const allRecommendationCaches: RecommendationsCache[] = [
  crossSellCache,
  flavourMeCache,
  upSellCache,
  popupCrossSellCache,
  popupFlavourMeCache,
  popupUpSellCache,
  homeFlavourMeCache,
  homePopupFlavourMeCache,
];
